import { BaseButton } from 'app/components/buttons/BaseButton';
import { Context, NotificationPosition, Notification, Snackbar } from './type';
import React, { createContext, useContext, useMemo, useState } from 'react';

interface Props {
  children: React.ReactNode;
}

const NotificationsContext = createContext<Context>({} as Context);

export function NotificationsProvider({ children }: Props) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [snackbar, setSnackbar] = useState<Snackbar>();

  const addNotification = (
    notification: React.ReactNode,
    position: NotificationPosition = 'screen-centered',
    duration: number | null = 6000,
    onClose?: (() => void) | null,
    customId?: string,
  ) => {
    const id = customId || Math.random().toString(36).slice(2, 11);

    const alreadyExists = notifications.some(n => n.id === id);
    if (alreadyExists) return id;

    setNotifications(prev => [
      ...prev,
      {
        id,
        position,
        notification: (
          <BaseButton
            key={id}
            fullWidth
            onClick={() => {
              removeNotification(id);
              onClose?.();
            }}
          >
            {notification}
          </BaseButton>
        ),
      },
    ]);

    if (duration === null) return id;

    setTimeout(() => {
      removeNotification(id);
    }, duration);

    return id;
  };

  const addSnackbar = (
    snackbar: React.ReactNode,
    position: NotificationPosition = 'screen-centered',
    duration = 6000,
  ) => {
    setSnackbar({ content: snackbar, position });

    setTimeout(() => {
      removeSnackbar();
    }, duration);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const removeSnackbar = () => {
    setSnackbar(undefined);
  };

  const screenCenteredNotifications = useMemo(
    () => notifications.filter(n => n.position === 'screen-centered'),
    [notifications],
  );

  const contentCenteredNotifications = useMemo(
    () => notifications.filter(n => n.position === 'content-centered'),
    [notifications],
  );

  return (
    <NotificationsContext.Provider
      value={{
        screenCenteredNotifications,
        contentCenteredNotifications,
        snackbar,
        addNotification,
        addSnackbar,
        removeNotification,
        removeSnackbar,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

const useNotificationsContext = () => useContext(NotificationsContext);
export default useNotificationsContext;
